/**
 * Cognito Configuration
 * AWS Cognito authentication configuration
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  ISignUpResult,
  ICognitoUserPoolData,
} from "amazon-cognito-identity-js";

// Cognito configuration from environment variables
const cognitoConfig: ICognitoUserPoolData = {
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || "",
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || "",
};

// Export configuration values for reference
export const COGNITO_USER_POOL_ID = cognitoConfig.UserPoolId;
export const COGNITO_CLIENT_ID = cognitoConfig.ClientId;
export const COGNITO_REGION = import.meta.env.VITE_AWS_REGION || "eu-central-1";
// Full hosted UI domain (custom domain like "auth.example.com" or prefix like "myapp-dev")
const rawDomain = import.meta.env.VITE_COGNITO_DOMAIN || "dmy-learning-platform";
export const COGNITO_DOMAIN = rawDomain.includes(".") ? rawDomain : `${rawDomain}.auth.${COGNITO_REGION}.amazoncognito.com`;

// OAuth redirect URIs
const getRedirectUri = () => {
  const baseUrl = window.location.origin;
  return `${baseUrl}/auth/callback`;
};

const getLogoutUri = () => {
  return window.location.origin;
};

// Check if Cognito is configured
export const isCognitoConfigured = (): boolean => {
  return !!(cognitoConfig.UserPoolId && cognitoConfig.ClientId);
};

// Lazy-initialize User Pool (only when Cognito is actually used)
let _userPool: CognitoUserPool | null = null;

export const getUserPool = (): CognitoUserPool => {
  if (!_userPool) {
    if (!isCognitoConfigured()) {
      throw new Error(
        "Cognito not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID environment variables."
      );
    }
    _userPool = new CognitoUserPool(cognitoConfig);
  }
  return _userPool;
};

// Helper type for user attributes
export interface CognitoUserAttributes {
  email: string;
  name?: string;
  "custom:role"?: "STUDENT" | "ADMIN" | "INSTRUCTOR";
  email_verified?: boolean;
}

// Decoded ID token payload interface
export interface DecodedIdTokenPayload {
  sub: string;
  email: string;
  email_verified: boolean;
  "custom:role"?: "STUDENT" | "ADMIN" | "INSTRUCTOR";
  name?: string;
  aud: string;
  token_use: string;
  auth_time: number;
  exp: number;
  iat: number;
}

/**
 * Get current authenticated user (if any)
 */
export const getCurrentUser = (): CognitoUser | null => {
  if (!isCognitoConfigured()) return null;
  return getUserPool().getCurrentUser();
};

/**
 * Get current session (includes tokens)
 */
export const getCurrentSession = (): Promise<CognitoUserSession | null> => {
  return new Promise((resolve) => {
    const user = getCurrentUser();
    if (!user) {
      resolve(null);
      return;
    }

    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve(null);
        return;
      }
      resolve(session);
    });
  });
};

/**
 * Get ID token from current session
 */
export const getIdToken = async (): Promise<string | null> => {
  const session = await getCurrentSession();
  if (!session) return null;
  return session.getIdToken().getJwtToken();
};

/**
 * Get decoded ID token payload
 */
export const getDecodedIdToken = async (): Promise<DecodedIdTokenPayload | null> => {
  const session = await getCurrentSession();
  if (!session) return null;
  return session.getIdToken().decodePayload() as DecodedIdTokenPayload;
};

/**
 * Sign in with email and password
 */
export const signIn = (email: string, password: string): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        resolve(session);
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes, requiredAttributes) => {
        // Handle new password required (for admin-created users)
        reject({
          code: "NewPasswordRequired",
          message: "New password required",
          userAttributes,
          requiredAttributes,
          cognitoUser,
        });
      },
    });
  });
};

/**
 * Complete new password challenge
 */
export const completeNewPasswordChallenge = (
  cognitoUser: CognitoUser,
  newPassword: string,
  userAttributes: Record<string, string> = {}
): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session) => {
        resolve(session);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Sign up new user
 */
export const signUp = (
  email: string,
  password: string,
  attributes?: { name?: string },
  clientMetadata?: Record<string, string>
): Promise<ISignUpResult> => {
  return new Promise((resolve, reject) => {
    const attributeList: CognitoUserAttribute[] = [];

    attributeList.push(
      new CognitoUserAttribute({
        Name: "email",
        Value: email,
      })
    );

    if (attributes?.name) {
      attributeList.push(
        new CognitoUserAttribute({
          Name: "name",
          Value: attributes.name,
        })
      );
    }

    getUserPool().signUp(
      email,
      password,
      attributeList,
      [],
      (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result!);
      },
      clientMetadata
    );
  });
};

/**
 * Confirm sign up with verification code
 */
export const confirmSignUp = (email: string, code: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });

    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
};

/**
 * Resend confirmation code
 */
export const resendConfirmationCode = (
  email: string,
  clientMetadata?: Record<string, string>
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });

    cognitoUser.resendConfirmationCode(
      (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      },
      clientMetadata
    );
  });
};

/**
 * Initiate forgot password flow
 */
export const forgotPassword = (
  email: string,
  clientMetadata?: Record<string, string>
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });

    cognitoUser.forgotPassword(
      {
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        },
      },
      clientMetadata
    );
  });
};

/**
 * Confirm forgot password with code and new password
 */
export const confirmForgotPassword = (
  email: string,
  code: string,
  newPassword: string
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Change password (when already signed in)
 */
export const changePassword = (oldPassword: string, newPassword: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) {
      reject(new Error("No user is currently signed in"));
      return;
    }

    // Need to get session first to make authenticated call
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err || new Error("Failed to get session"));
        return;
      }

      user.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve();
      });
    });
  });
};

/**
 * Sign out current user
 */
export const signOut = (): void => {
  const user = getCurrentUser();
  if (user) {
    user.signOut();
  }
};

/**
 * Global sign out (revokes all tokens on all devices)
 */
export const globalSignOut = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) {
      resolve();
      return;
    }

    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        // If no valid session, just do local sign out
        user.signOut();
        resolve();
        return;
      }

      user.globalSignOut({
        onSuccess: () => {
          resolve();
        },
        onFailure: (err) => {
          // Still do local signout even if global fails
          user.signOut();
          reject(err);
        },
      });
    });
  });
};

/**
 * Refresh the session tokens
 */
export const refreshSession = (): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) {
      reject(new Error("No user is currently signed in"));
      return;
    }

    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err || new Error("Failed to get session"));
        return;
      }

      const refreshToken = session.getRefreshToken();

      user.refreshSession(refreshToken, (err, newSession) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(newSession);
      });
    });
  });
};

/**
 * Get user attributes
 */
export const getUserAttributes = (): Promise<Record<string, string>> => {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) {
      reject(new Error("No user is currently signed in"));
      return;
    }

    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        reject(err || new Error("Failed to get session"));
        return;
      }

      user.getUserAttributes((err, attributes) => {
        if (err) {
          reject(err);
          return;
        }

        const attrs: Record<string, string> = {};
        attributes?.forEach((attr) => {
          attrs[attr.getName()] = attr.getValue();
        });

        resolve(attrs);
      });
    });
  });
};

/**
 * Initiate Google OAuth sign-in flow
 * Redirects user to Cognito hosted UI with Google as the identity provider
 */
export const signInWithGoogle = (): void => {
  const domain = COGNITO_DOMAIN;
  const redirectUri = encodeURIComponent(getRedirectUri());
  const responseType = "code";
  const scope = encodeURIComponent("email openid profile");

  const url = `https://${domain}/oauth2/authorize?identity_provider=Google&redirect_uri=${redirectUri}&response_type=${responseType}&client_id=${COGNITO_CLIENT_ID}&scope=${scope}&prompt=login`;

  window.location.href = url;
};

/**
 * Exchange authorization code for tokens
 * Called after OAuth redirect with code in URL
 */
export const exchangeCodeForTokens = async (code: string): Promise<{
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}> => {
  const domain = COGNITO_DOMAIN;
  const redirectUri = getRedirectUri();

  const response = await fetch(`https://${domain}/oauth2/token`, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: COGNITO_CLIENT_ID,
      code,
      redirect_uri: redirectUri,
    }),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Token exchange failed: ${error}`);
  }

  return response.json();
};

/**
 * Create a CognitoUserSession from OAuth tokens
 * This allows using the session with the standard Cognito SDK methods
 */
export const createSessionFromTokens = (tokens: {
  access_token: string;
  id_token: string;
  refresh_token: string;
}): CognitoUserSession => {
  const accessToken = new CognitoAccessToken({ AccessToken: tokens.access_token });
  const idToken = new CognitoIdToken({ IdToken: tokens.id_token });
  const refreshToken = new CognitoRefreshToken({ RefreshToken: tokens.refresh_token });

  return new CognitoUserSession({
    AccessToken: accessToken,
    IdToken: idToken,
    RefreshToken: refreshToken,
  });
};

/**
 * Set the session for a federated user (Google OAuth)
 */
export const setFederatedSession = (
  username: string,
  session: CognitoUserSession
): void => {
  const userPool = getUserPool();
  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });

  // Set the sign-in user session
  cognitoUser.setSignInUserSession(session);
};

/**
 * Cognito hosted UI logout
 */
export const hostedUILogout = (): void => {
  const domain = COGNITO_DOMAIN;
  const logoutUri = encodeURIComponent(getLogoutUri());

  // First do local signout
  signOut();

  // Then redirect to Cognito logout endpoint
  window.location.href = `https://${domain}/logout?client_id=${COGNITO_CLIENT_ID}&logout_uri=${logoutUri}`;
};

// Export types
export { CognitoUser, CognitoUserSession, CognitoUserAttribute };
